import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { DisplayComponent } from 'formlabs-web-components/BaseComponents';
import { Link } from 'formlabs-web-components/Link';
import { GridContainer, Grid, Cell } from 'formlabs-web-components/Grid';

import useIsMobile from '../../hooks/useIsMobile';
import useObserver from '../../hooks/useObserver';

import './TableOfContents.module.css';

const TableOfContents = ({ children, levels }) => {
  const { t } = useTranslation();
  const ref = useRef();
  const [toc, setToc] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const isMobile = useIsMobile(960);

  const [attach, detach] = useObserver(
    (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    },
    { rootMargin: '0% 0% -80% 0%' }
  );

  const headerLevels = (levels || [1, 2, 3, 4, 5, 6]).map(level => 'h' + level);
  const leastLevel = (levels && Math.min(...levels)) || 1;

  // We have to act on rendered content because deep children structure does not allow for introspection
  useEffect(
    () => {
      if (!isMobile) {
        const content = ref.current;
        const allHeaders = content.querySelectorAll(headerLevels.join(','));

        const links = [...allHeaders].map(header => {
          const text = header.innerText;

          const id = header.id || encodeURIComponent(text);
          if (!header.id) {
            header.id = id;
          }

          attach(header);

          return {
            level: parseInt(header.tagName[1]) - leastLevel,
            id,
            text
          };
        });

        setToc(links);

        return () => {
          [...allHeaders].forEach(header => detach(header));
        };
      }
    },
    [children, isMobile]
  );

  if (isMobile) {
    return (
      <GridContainer>
        <Grid direction="x" primaryAlignment="align-left">
          <Cell smallSize={24}>{children}</Cell>
        </Grid>
      </GridContainer>
    );
  }

  return (
    <GridContainer>
      <Grid direction="x" primaryAlignment="align-left">
        <Cell smallSize={6}>
          <div styleName="toc">
            <p>{t('Table of Contents')}</p>
            <nav>
              <ul>
                {toc.map(entry => (
                  <li
                    key={entry.id}
                    styleName={classNames({
                      active: activeSection === entry.id
                    })}
                    style={{ paddingLeft: entry.level + 'rem' }}
                  >
                    <Link to={`#${entry.id}`}>{entry.text}</Link>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </Cell>
        <Cell smallSize={18}>
          <div ref={ref}>{children}</div>
        </Cell>
      </Grid>
    </GridContainer>
  );
};

export default DisplayComponent(TableOfContents);
